import { HorizontalBarWidget } from "../charts/HorizontalBarWidget";
import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";

interface WorkloadRow {
  userId: string;
  userName: string;
  roleName: string | null;
  openTasks: number;
  overdueTasks: number;
  completedTasks: number;
  openActions: number;
  overdueActions: number;
  meetingsThisMonth: number;
  meetingsAttended: number;
  workloadScore: number;
}

function workloadColor(score: number): string {
  if (score > 80) return "hsl(0,72%,55%)";
  if (score >= 50) return "hsl(38,92%,50%)";
  return "hsl(142,72%,40%)";
}

function ScorePill({ score }: { score: number }) {
  const color = workloadColor(score);
  const bg =
    score > 80
      ? "hsl(0,84%,95%)"
      : score >= 50
      ? "hsl(38,92%,92%)"
      : "hsl(142,55%,92%)";
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: bg, color }}
    >
      {score}
    </span>
  );
}

export function TeamWorkloadReport({ data }: { data: WorkloadRow[] }) {
  const total = data.length;
  const avgOpenTasks =
    total > 0 ? Math.round(data.reduce((s, r) => s + r.openTasks, 0) / total) : 0;
  const avgOpenActions =
    total > 0
      ? Math.round(data.reduce((s, r) => s + r.openActions, 0) / total)
      : 0;
  const sorted = [...data].sort((a, b) => b.workloadScore - a.workloadScore);
  const mostLoaded = sorted[0]?.userName ?? "—";
  const leastLoaded = sorted.length ? sorted[sorted.length - 1].userName : "—";

  const chartData = data.slice(0, 20).map((d) => ({
    name: d.userName,
    score: d.workloadScore,
  }));

  const cols: Array<TableColumn<WorkloadRow>> = [
    { key: "userName", label: "Team Member" },
    { key: "roleName", label: "Role", render: (r) => r.roleName ?? "—" },
    { key: "openTasks", label: "Open Tasks", align: "right" },
    {
      key: "overdueTasks",
      label: "Overdue",
      align: "right",
      render: (r) =>
        r.overdueTasks > 0 ? (
          <span className="text-[hsl(0,72%,45%)] font-medium">
            {r.overdueTasks}
          </span>
        ) : (
          0
        ),
    },
    { key: "openActions", label: "Open Actions", align: "right" },
    {
      key: "overdueActions",
      label: "Overdue",
      align: "right",
      render: (r) =>
        r.overdueActions > 0 ? (
          <span className="text-[hsl(0,72%,45%)] font-medium">
            {r.overdueActions}
          </span>
        ) : (
          0
        ),
    },
    { key: "meetingsThisMonth", label: "Meetings", align: "right" },
    {
      key: "workloadScore",
      label: "Workload",
      align: "right",
      render: (r) => <ScorePill score={r.workloadScore} />,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Team Members" value={total} />
        <StatCard label="Avg Open Tasks" value={avgOpenTasks} />
        <StatCard label="Avg Open Actions" value={avgOpenActions} />
        <StatCard label="Most Loaded" value={mostLoaded} tone="bad" />
        <StatCard label="Least Loaded" value={leastLoaded} tone="good" />
      </div>

      <ReportSection title="Workload Distribution">
        <HorizontalBarWidget
          data={chartData}
          yKey="name"
          xKey="score"
          height={Math.max(200, chartData.length * 28)}
          color={(row) => workloadColor(row.score as number)}
        />
      </ReportSection>

      <ReportSection title="Team Members">
        <ReportTable columns={cols} rows={data} rowKey={(r) => r.userId} />
      </ReportSection>
    </div>
  );
}
